import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Routes from '../../../../../resources/routes.json';

import { showFooter, showMenu } from '../../../store/actions/util-actions';
import { createRecurrent } from '../../../store/actions/customer-actions';

import { InstallmentCard } from '../../../components/features/installment-card';
import { CustomerHeader } from '../components/customer-header';
import { LoansPaidoffCardComponent } from '../components/loans-paidoff-card';
import WebChat from '../../../components/features/webchat/webchat';
import FloatInsuranceRequest from '../components/float-insurance-request/float-insurance-request';
import ChargedOff from '../components/charged-off/charged-off';
import OfflineUnlock from '../components/offline-unlock/offline-unlock';
import { companiesData } from '../components/charged-off/charge-companies-data';
import Loading from '../../../components/features/loading/loading';

import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as loanStateEnum from '../../../utils/enumerator/enum-loan-state';
import * as loanPhaseEnum from '../../../utils/enumerator/enum-loan-phase';
import { EMPRESTIMO_SIM } from '../../../utils/enumerator/enum-products';
import { isMobile } from '../../../utils/helper/page-behavior-helper';
import { setEventOnDataLayer } from 'utils/helper/datalayer-events-helper.js';

import { goToLoanRoute } from 'navigation/application-routes';

import styles from './styles.module.scss';

export default function CustomerHomePage() {
  const dispatch = useDispatch();
  const currentLoan = useSelector((state) => state.personalLending.loan);
  const rePayments = useSelector((state) => state.personalLending.rePayments);
  const device = useSelector((state) => state.customer.device);

  const nextUnpaidInstallment = rePayments?.nextUnpaidInstallment;
  const loanId = currentLoan.id;
  const loanStateId = currentLoan?.state?.id;
  const loanPhaseId = currentLoan?.state?.phase?.id;
  const loanTerm = rePayments?.balance?.length;
  const installmentsPayment = currentLoan?.conditions?.installmentsPayment;
  const currentLoanIsInCollectionPhase = [loanPhaseEnum.COLLECTION].includes(loanPhaseId);
  const notInRepaidPhase = ![loanPhaseEnum.RE_PAID].includes(loanPhaseId) && nextUnpaidInstallment;
  const currentLoanIsChargedOff = [loanStateEnum.CHARGED_OFF].includes(loanStateId);
  const chargeBuyerCompany = companiesData?.filter((item) => item.name === rePayments?.portfolioSellInfo?.buyer)[0];
  const loanFullyPaidOff = !!(
    loanStateId === loanStateEnum.ON_TIME_FULLY_PAID_OFF || loanStateId === loanStateEnum.LATE_FULLY_PAID_OFF
  );

  const [showCurrentSlip, setShowCurrentSlip] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(showFooter(false));
    dispatch(showMenu(false));
    setEventOnDataLayer('customerArea', currentLoan);
    if (loanFullyPaidOff) {
      setLoading(true);
      dispatch(createRecurrent(device)).then(() => {
        goToLoanRoute(currentLoan, Routes.CUSTOMER_PAGE, Routes.LOAN_APPLICATION_CONTRACT);
        setLoading(false);
      });
    }
  }, []);

  if (loading) {
    return (
      <>
        <Loading title="Aguarde um momento, por favor..." />
      </>
    );
  }

  if (currentLoanIsChargedOff) {
    return (
      <ChargedOff
        title={chargeBuyerCompany?.title || companiesData[0]?.title}
        description={chargeBuyerCompany?.description() || companiesData[0]?.description()}
        selfService={chargeBuyerCompany?.selfService || companiesData[0]?.selfService}
        onlineChat={chargeBuyerCompany?.onlineChat || companiesData[0]?.onlineChat}
        appDescription={chargeBuyerCompany?.appDescription() || companiesData[0]?.appDescription()}
        email={chargeBuyerCompany?.email || companiesData[0]?.email}
        phoneNumber={chargeBuyerCompany?.phoneNumber || companiesData[0]?.phoneNumber}
        onClickPhoneNumber={chargeBuyerCompany?.onClickPhoneNumber || companiesData[0]?.onClickPhoneNumber}
        whatsapp={chargeBuyerCompany?.whatsapp || companiesData[0]?.whatsapp}
        onClickWhatsapp={chargeBuyerCompany?.onClickWhatsapp || companiesData[0]?.onClickWhatsapp}
      />
    );
  }

  return (
    <div className={styles.loggedInArea}>
      <CustomerHeader
        withMobileCollateral={currentLoan?.application?.collateral?.withMobileCollateral}
        loanPhase={loanPhaseId}
        personName={currentLoan?.application?.personal?.name}
        device={device}
        setShowCurrentSlip={setShowCurrentSlip}
      />

      {currentLoanIsInCollectionPhase && <WebChat />}

      <div className={styles.content}>
        <div className={styles.currentLoanLink}>
          <NavLink to={Routes.CUSTOMER_LOAN_PAGE.DEFAULT}>
            <span className="text-semibold">Meu empréstimo &nbsp;&nbsp;</span>
            <FontAwesomeIcon icon={faChevronRight} size="sm" />
          </NavLink>
        </div>

        <div className={styles.contentCardsPanel}>
          {notInRepaidPhase ? (
            <InstallmentCard
              installment={nextUnpaidInstallment}
              installmentsPayment={installmentsPayment}
              totalInstallments={loanTerm}
              loanId={loanId}
              customWidth={isMobile() && '100%'}
              showCurrentSlip={showCurrentSlip}
              setShowCurrentSlip={setShowCurrentSlip}
            />
          ) : (
            <LoansPaidoffCardComponent createRecurrent={() => dispatch(createRecurrent(device))} />
          )}
        </div>
      </div>
      {notInRepaidPhase && <FloatInsuranceRequest loan={currentLoan} device={device} />}
    </div>
  );
}
